import { useLocation, useMatches } from "@tanstack/react-router";
import { pickBoard } from "@/boards/board-switch";
import { boardsAtPath } from "@/boards/registry";
import type { BoardDef } from "@/boards/types";
import type { StaffRole } from "@/contracts/permissions";
import { homeFor, NAV, spacesFor, type NavItem, type Space } from "./nav";

const SPACE_KEY = "melrys.admin.space";

function readSpace(): Space | null {
  try {
    return (sessionStorage.getItem(SPACE_KEY) as Space | null) ?? null;
  } catch {
    return null;
  }
}
function writeSpace(s: Space) {
  try {
    sessionStorage.setItem(SPACE_KEY, s);
  } catch {
    /* private mode */
  }
}

/** The board on screen (null on non-board routes such as /plateforme). */
export function useActiveBoard(): BoardDef | null {
  const matches = useMatches();
  const loc = useLocation();
  const leaf = matches[matches.length - 1];
  const path = (leaf as { fullPath?: string } | undefined)?.fullPath ?? "";
  const boards = boardsAtPath(path.length > 1 ? path.replace(/\/$/, "") : path);
  return pickBoard(boards, loc.pathname, loc.searchStr) ?? null;
}

/** Sidebar item whose `to` is the longest prefix of the path (fallback for routes without a board). */
function navKeyByPath(pathname: string): string | null {
  let best: { key: string; len: number } | null = null;
  const walk = (i: NavItem) => {
    if (!i.children && (pathname === i.to || pathname.startsWith(i.to + "/")) && (!best || i.to.length > best.len)) best = { key: i.key, len: i.to.length };
    i.children?.forEach(walk);
  };
  Object.values(NAV).forEach((secs) => secs.forEach((s) => s.items.forEach(walk)));
  return (best as { key: string } | null)?.key ?? null;
}

/** Current space + lit nav item. A board with `space: null` (Finance, Paramètres…) keeps the last space. */
export function useShellPosition(role: StaffRole): { space: Space; spaces: Space[]; navKey: string | null; board: BoardDef | null } {
  const board = useActiveBoard();
  const { pathname } = useLocation();
  const spaces = spacesFor(role);
  // Default: the space of the role's home (ADMIN → Compta, as Admin-Login-Code lands on the dashboard).
  const homeSpace = boardsAtPath(homeFor(role))[0]?.space;
  let space: Space = homeSpace && spaces.includes(homeSpace) ? homeSpace : (spaces[0] ?? "creation");
  const remembered = readSpace();
  if (board?.space && spaces.includes(board.space)) space = board.space;
  else if (remembered && spaces.includes(remembered)) space = remembered;
  if (board?.space && spaces.includes(board.space) && remembered !== board.space) writeSpace(board.space);
  return { space, spaces, navKey: board?.nav ?? navKeyByPath(pathname), board };
}

export function rememberSpace(s: Space) {
  writeSpace(s);
}
